<template>
  <v-select
    v-bind="$attrs"
    :modelValue="modelValue"
    @update:modelValue="handleUpdate"
    :options="options"
    :label="label"
    :track-by="trackBy"
    :reduce="reduce"
    :clearable="clearable"
    :searchable="searchable"
    :filterable="filterable"
    :multiple="multiple"
    :disabled="disabled"
    :placeholder="placeholder"
    :loading="loading"
    append-to-body
    class="w-full"
    @option:selected="(opt) => $emit('option:selected', opt)"
    @option:selecting="(opt) => $emit('option:selecting', opt)"
    @search="(query, loadingFn) => $emit('search', query, loadingFn)"
    @input="(val) => $emit('input', val)"
    :calculate-position="withPopper"
  >
    <template v-if="$slots.option" #option="slotProps">
      <slot name="option" v-bind="slotProps" />
    </template>
  </v-select>
</template>

<script lang="ts" setup>
import { createPopper, Placement } from "@popperjs/core";
import { defineEmits, defineProps } from "vue";

defineProps({
  modelValue: {
    type: [Array, Object, String, Number, null],
    default: null,
  },
  options: {
    type: Array,
    required: true,
  },
  label: {
    type: String,
    default: "label",
  },
  trackBy: {
    type: String,
    default: "id",
  },
  reduce: {
    type: Function,
    default: undefined,
  },
  clearable: {
    type: Boolean,
    default: true,
  },
  searchable: {
    type: Boolean,
    default: true,
  },
  filterable: {
    type: Boolean,
    default: true,
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: "Select",
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const placement: Placement = "bottom";
const emit = defineEmits([
  "update:modelValue",
  "option:selected",
  "option:selecting",
  "search",
  "input",
  "clear",
]);

const handleUpdate = (val: any) => {
  emit("update:modelValue", val);
  if (val === null || (Array.isArray(val) && val.length === 0)) {
    emit("clear");
  }
};

function withPopper(
  dropdownList: HTMLElement,
  component: any,
  { width }: { width: string },
) {
  dropdownList.style.width = width;

  const popper = createPopper(component.$refs.toggle, dropdownList, {
    placement: placement,
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, -1],
        },
      },
      {
        name: "toggleClass",
        enabled: true,
        phase: "write",
        fn({ state }) {
          component.$el.classList.toggle("drop-up", state.placement === "top");
        },
      },
    ],
  });

  return () => popper.destroy();
}
</script>

<style>
@import "vue-select/dist/vue-select.css";
.v-select.drop-up.vs--open .vs__dropdown-toggle {
  border-radius: 0 0 4px 4px;
  border-top-color: transparent;
  border-bottom-color: rgba(60, 60, 60, 0.26);
}

[data-popper-placement="top"] {
  border-radius: 4px 4px 0 0;
  border-top-style: solid;
  border-bottom-style: none;
  box-shadow: 0 -3px 6px rgba(0, 0, 0, 0.15);
}
</style>
